/**
 * FactoryUI — the shared design-system primitives for the Content
 * Factory theme. Every Phase 3 page uses these instead of hand-rolling
 * its own layout so the "industrial monitoring dashboard" language
 * stays consistent across the app.
 *
 * All primitives are pure display — zero business logic. They accept
 * children + variant props and render the visual chrome.
 */
import { forwardRef } from "react";
import { cn } from "@/lib/utils";

/**
 * FactorySection — the outer wrapper for every content block.
 * Adds:
 *   • "// STATION.NN — SECTION NAME" mono header (station-numbered so
 *     the whole page reads like a production-line diagram)
 *   • Optional description under the header
 *   • Hairline amber divider that fades from amber → transparent
 *     across the left 25% of the row (matches the factory conveyor
 *     visual language)
 *   • Optional right-slot for action CTAs
 */
export function FactorySection({
  station,      // "01", "02", ... — the station number chip
  title,        // "MONITORING GRID" — the uppercase heading
  description,  // 1-line subtitle
  actions,      // right-side slot (buttons, filters)
  children,
  className,
  "data-testid": testid,
}) {
  return (
    <section
      data-testid={testid}
      className={cn("relative pt-6 pb-8", className)}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-2">
            {station && (
              <span className="inline-flex items-center justify-center min-w-[2.75rem] h-7 px-2 font-mono text-[10px] font-black uppercase tracking-[0.25em] bg-yellow-400 text-black rounded-sm no-pill">
                {station}
              </span>
            )}
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-yellow-400">
              // {title}
            </span>
          </div>
          <div className="h-px w-24 bg-gradient-to-r from-yellow-400/70 via-yellow-400/20 to-transparent" />
          {description && (
            <p className="mt-3 text-sm text-zinc-400 max-w-2xl leading-relaxed">
              {description}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>
        )}
      </div>
      {children}
    </section>
  );
}

/**
 * StationCard — the primary content container. Feels like a control
 * panel: dark matte body, 1px zinc border that becomes amber on hover,
 * corner cut-outs, and an optional numbered station chip.
 */
export const StationCard = forwardRef(function StationCard(
  {
    stationNo,        // number label chip in top-left (optional)
    stationLabel,     // small mono label right of the number
    title,            // large white heading (optional)
    right,            // right-slot inside the card header
    footer,           // footer strip beneath the body
    hoverable = true, // amber border on hover
    active = false,   // permanently-amber border (selected state)
    className,
    children,
    "data-testid": testid,
    ...rest
  },
  ref,
) {
  return (
    <div
      ref={ref}
      data-testid={testid}
      className={cn(
        "relative bg-zinc-950/70 rounded-sm transition-colors",
        active
          ? "border border-yellow-400/70 shadow-[0_0_0_1px_rgba(255,193,7,0.25)]"
          : "border border-zinc-800",
        hoverable && !active && "hover:border-yellow-400/40",
        className,
      )}
      {...rest}
    >
      {/* Corner marks — 4 small L-brackets in the corners of the
          card, matching the industrial "instrument panel" motif from
          the Content Factory mock. */}
      <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-yellow-400/40 pointer-events-none" />
      <span className="absolute top-0 right-0 w-2 h-2 border-t border-r border-yellow-400/40 pointer-events-none" />
      <span className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-yellow-400/40 pointer-events-none" />
      <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-yellow-400/40 pointer-events-none" />

      {(stationNo !== undefined || title || right) && (
        <div className="flex items-center gap-3 px-4 pt-4 pb-3">
          {stationNo !== undefined && (
            <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 font-mono text-[10px] font-black uppercase tracking-[0.2em] bg-yellow-400 text-black rounded-sm no-pill flex-shrink-0">
              {stationNo}
            </span>
          )}
          <div className="min-w-0 flex-1">
            {stationLabel && (
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">
                {stationLabel}
              </div>
            )}
            {title && (
              <div className="font-bold text-zinc-100 text-sm leading-tight truncate">
                {title}
              </div>
            )}
          </div>
          {right && <div className="flex-shrink-0">{right}</div>}
        </div>
      )}

      <div className={cn(
        (stationNo !== undefined || title || right) ? "px-4 pb-4" : "p-4",
      )}>
        {children}
      </div>

      {footer && (
        <div className="border-t border-zinc-800 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-400">
          {footer}
        </div>
      )}
    </div>
  );
});

/**
 * HeroMetric — a large amber numeric readout with a mono label. Used
 * on the Dashboard hero grid to hammer home the key numbers (channels
 * tracked, total views, projected revenue).
 */
export function HeroMetric({
  label,        // "TRACKED CHANNELS" — small mono uppercase
  value,        // "12" — big display number
  unit,         // "channels" — small suffix (optional)
  delta,        // "+3 this week" — small subtitle (optional)
  positive = true,
  className,
  "data-testid": testid,
}) {
  return (
    <div
      data-testid={testid}
      className={cn(
        "relative bg-zinc-950/70 border border-zinc-800 rounded-sm p-5",
        "hover:border-yellow-400/40 transition-colors",
        className,
      )}
    >
      <span className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-yellow-400/70 pointer-events-none" />
      <span className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-yellow-400/40 pointer-events-none" />
      <div className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-1">
        {label}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-display text-4xl font-black text-yellow-400 leading-none tabular-nums">
          {value}
        </span>
        {unit && (
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            {unit}
          </span>
        )}
      </div>
      {delta && (
        <div className={cn(
          "mt-1 font-mono text-[10px] uppercase tracking-[0.2em]",
          positive ? "text-emerald-300" : "text-red-300",
        )}>
          {delta}
        </div>
      )}
    </div>
  );
}

/**
 * StatusChip — small pill badge with amber accent for status labels.
 * Variants pick the border/text color; the pill radius + mono
 * typography stay consistent across the app.
 */
export function StatusChip({
  variant = "amber",   // amber | emerald | red | zinc
  children,
  className,
  "data-testid": testid,
}) {
  const map = {
    amber:   "border-yellow-400/60 text-yellow-300 bg-yellow-400/5",
    emerald: "border-emerald-400/60 text-emerald-300 bg-emerald-400/5",
    red:     "border-red-400/60 text-red-300 bg-red-400/5",
    zinc:    "border-zinc-700 text-zinc-300 bg-zinc-900/50",
  };
  return (
    <span
      data-testid={testid}
      className={cn(
        "inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] px-2.5 py-1 rounded-full border",
        map[variant] || map.amber,
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * ConveyorDivider — horizontal amber-to-transparent hairline with a
 * mono station-index label. Slot it between FactorySections to visually
 * segment a page like a factory conveyor.
 */
export function ConveyorDivider({ label = "" }) {
  return (
    <div className="flex items-center gap-3 my-2 select-none">
      <span className="h-px flex-1 bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />
      {label && (
        <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-zinc-600">
          // {label}
        </span>
      )}
      <span className="h-px flex-1 bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />
    </div>
  );
}
